import {
  Baby,
  BriefcaseBusiness,
  CheckCircle2,
  ClipboardList,
  FileText,
  GraduationCap,
  HeartHandshake,
  Linkedin,
  MessageCircle,
  RefreshCw,
  SearchCheck,
  Sparkles,
  Target,
  Users,
} from "lucide-react";
import { images, packagePrices } from "../config/site";

export const navLinks = [
  { label: "Home", path: "/", testId: "home" },
  { label: "About Ben", path: "/about", testId: "about-ben" },
  { label: "Services", path: "/services", testId: "services" },
  { label: "Packages", path: "/packages", testId: "packages" },
  { label: "Success Stories", path: "/#success-stories", testId: "success-stories" },
  { label: "Contact", path: "/contact", testId: "contact" },
  { label: "Resources", path: "/resources", testId: "resources" },
    { label: "ATS CV Checker", path: "/ats-cv-checker", testId: "ats-cv-checker" },
];

export const seekerCards = [
  { title: "Adults returning to work after a break", icon: RefreshCw },
  { title: "People changing careers or direction", icon: BriefcaseBusiness },
  { title: "Parents and carers supporting someone into work", icon: Baby },
  { title: "Over-40s and over-50s rebuilding confidence", icon: Sparkles },
  { title: "Young people aged 16–24 seeking first jobs, apprenticeships or traineeships", icon: GraduationCap },
  { title: "People with employment gaps who need help explaining their story", icon: MessageCircle },
  { title: "People applying for jobs but not hearing back", icon: SearchCheck },
  { title: "Churches, community groups and local organisations supporting jobseekers", icon: HeartHandshake },
];

export const serviceCards = [
  {
    slug: "cv-review-rewrite",
    title: "CV Review & Rewrite",
    icon: FileText,
    text: "Employability-led CV support to make your experience clearer, stronger and better matched to the roles you want.",
  },
  {
    slug: "cover-letter-support",
    title: "Cover Letter Support",
    icon: ClipboardList,
    text: "Create a focused cover letter that sounds professional, relevant and personal without becoming generic.",
  },
  {
    slug: "job-application-support",
    title: "Job Application Support",
    icon: Target,
    text: "Get practical support with online applications, supporting statements and questions so your experience is presented clearly.",
  },
  {
    slug: "interview-preparation",
    title: "Interview Preparation",
    icon: Users,
    text: "Practise interview questions, structure stronger answers and build confidence before speaking to employers.",
  },
  {
    slug: "linkedin-profile-support",
    title: "LinkedIn Profile Support",
    icon: Linkedin,
    text: "Improve your LinkedIn headline, About section and work history so your profile supports your wider job search.",
  },
  {
    slug: "young-person-career-starter",
    title: "Young Person Career Starter Support",
    icon: GraduationCap,
    text: "Modern job-search coaching for young people aged 16–24 applying for first jobs, apprenticeships or early career roles.",
  },
];

export const moreThanCvCards = [
  { title: "Find your strengths", text: "Help identifying your skills, experience and transferable strengths.", icon: SearchCheck },
  { title: "Target the right roles", text: "Support choosing suitable jobs, apprenticeships, entry-level roles or career-change options.", icon: Target },
  { title: "Create stronger applications", text: "CVs, cover letters, supporting statements and online application support.", icon: FileText },
  { title: "Prepare for interviews", text: "Practical interview questions, answer structure and confidence-building.", icon: Users },
  { title: "Use AI wisely", text: "AI-assisted drafting with human review, so your application does not sound generic.", icon: Sparkles },
  { title: "Build a clear action plan", text: "Simple next steps so you know what to do after the session.", icon: ClipboardList },
];

export const communitySupportCards = [
  { title: "Parents and carers", text: "Support for families helping young people take practical next steps into work or training.", icon: Baby, image: images.parentsCarersSupport, alt: "Parent or carer supporting a young person with Job Boost guidance" },
  { title: "Young people aged 16–24", text: "Guidance for first jobs, apprenticeships, traineeships and early work opportunities.", icon: GraduationCap, image: images.youngPeopleAchievementCard, alt: "Young person receiving a Job Boost by Ben achievement award" },
  { title: "Churches and community groups", text: "Practical job-search guidance for groups supporting people who need confidence and direction.", icon: HeartHandshake, image: images.churchCommunitySupport, alt: "Community group employability support discussion in a welcoming setting" },
  { title: "Local organisations", text: "Support for organisations helping people move back towards work, training or better opportunities.", icon: Users, image: images.localOrganisationsSupport, alt: "Local organisation partnership meeting for practical job-search support" },
];

export const whyChoose = [
  "Real experience supporting UK jobseekers",
  "Practical help with CVs, applications and interviews",
  "Support for young people, adults and career changers",
  "Clear, friendly guidance without judgement",
  "Remote support available across the UK",
  "Documents shaped around your real experience",
];

export const howItWorks = [
  { title: "Send a message", text: "Contact Ben on WhatsApp and explain what you need help with." },
  { title: "Share your CV or details", text: "Send your CV, job advert, application question or interview details." },
  { title: "Get practical support", text: "Your CV, application or interview plan is shaped around your goals, experience and the type of role you want." },
  { title: "Move forward with confidence", text: "You receive clearer wording, stronger structure and practical next steps you can use." },
];

export const packages = [
  { slug: "cv-review", title: "CV Review", price: packagePrices.cvReview, outcome: "Best for improving your CV before applying", text: "Best if you already have a CV but need employability-led feedback, clearer wording and practical next steps." },
  { slug: "cv-rewrite", title: "CV Rewrite", price: packagePrices.cvRewrite, outcome: "Best for a stronger, more confident CV structure", text: "Best if your CV needs stronger structure, more confident wording and a professional layout shaped around your goals." },
  { slug: "cv-cover-letter", title: "CV + Cover Letter", price: packagePrices.cvCoverLetter, outcome: "Best for getting fuller support with targeted applications", text: "Best if you are applying for a specific role and want both documents aligned, relevant and believable." },
  { slug: "interview-preparation-session", title: "Interview Preparation Session", price: packagePrices.interviewPrep, outcome: "Best for preparing confidently before an interview", text: "Best if you have an interview coming up and want practical coaching, answer structure and confidence-building." },
  { slug: "linkedin-profile-refresh", title: "LinkedIn Profile Refresh", price: packagePrices.linkedInRefresh, outcome: "Best for strengthening your online job-search presence", text: "Best if you want your LinkedIn profile to support your job search and present your experience more professionally." },
  { slug: "young-person-starter-support", title: "Young Person Starter Support", price: packagePrices.youngPersonStarter, outcome: "Best for young people taking their first steps into work", text: "Job-search support for young people aged 16–24 applying for first jobs, apprenticeships, traineeships or early work opportunities." },
];

export const detailedServices = [
  {
    slug: "cv-review-rewrite",
    title: "CV Review & Rewrite",
    intro: "Employability-led CV writing help for UK jobseekers who want their experience to read with more confidence, focus and relevance.",
    for: "People who already have a CV, people returning to work, career changers, or anyone applying but not hearing back.",
    helps: ["Structure your CV around the jobs you want", "Improve wording, clarity and impact", "Use modern tools wisely without losing your real voice"],
  },
  {
    slug: "cover-letter-support",
    title: "Cover Letter Support",
    intro: "Focused cover letter support that keeps your message professional, relevant and human in a world of generic applications.",
    for: "Jobseekers applying for a specific role who want to explain their fit clearly and personally.",
    helps: ["Shape a strong opening", "Connect your experience to the job advert", "Avoid vague or copied wording"],
  },
  {
    slug: "job-application-support",
    title: "Job Application Support",
    intro: "Practical job application help for online forms, supporting statements and answer boxes that need clear, confident examples.",
    for: "People filling in online forms, apprenticeship applications, NHS-style applications or competency questions.",
    helps: ["Break down application questions", "Choose stronger examples", "Write answers that feel clear and relevant"],
  },
  {
    slug: "interview-preparation",
    title: "Interview Preparation",
    intro: "Interview preparation that helps you practise, structure answers and feel more confident speaking about your experience.",
    for: "Anyone with an interview coming up, people who feel nervous, or jobseekers who struggle to explain themselves aloud.",
    helps: ["Practise common questions", "Use simple answer structures", "Build confidence around your real experience"],
  },
  {
    slug: "linkedin-profile-support",
    title: "LinkedIn Profile Support",
    intro: "LinkedIn profile support for making your headline, About section and work history support a modern job search.",
    for: "Professionals, career changers, graduates and jobseekers who want a clearer online profile.",
    helps: ["Improve your headline", "Rewrite your About section", "Present work history in a more professional way"],
  },
  {
    slug: "young-person-career-starter",
    title: "Young Person Career Starter Support",
    intro: "Modern job-search support for young people aged 16–24 applying for first jobs, apprenticeships, traineeships or early career opportunities.",
    for: "Young people starting out, and parents or carers who want practical CV and interview help for their child.",
    helps: ["Create or improve a first CV", "Prepare for starter interviews", "Explain school, volunteering, hobbies and early experience well"],
    image: images.servicesYoungPersonCertificates,
    alt: "Ben celebrating a young person's Job Boost achievement award",
  },
];

export const quizQuestions = [
  {
    id: "need",
    question: "What do you need help with most right now?",
    options: ["My CV", "A cover letter", "Job applications", "Interviews", "LinkedIn", "I am not sure"],
  },
  {
    id: "situation",
    question: "What best describes your situation?",
    options: ["I am returning to work after a break", "I am changing jobs or careers", "I am applying for my first job", "I am helping my child or someone else", "I have applied for jobs but I am not hearing back", "I have an interview coming up"],
  },
  {
    id: "confidence",
    question: "How confident do you feel about applying for jobs?",
    options: ["Very confident", "Somewhat confident", "Not very confident", "I feel stuck and need guidance"],
  },
  {
    id: "cv",
    question: "Do you already have a CV?",
    options: ["Yes, but it needs improving", "Yes, but I am not sure if it is good enough", "No, I need one created", "I need help tailoring it to a job"],
  },
  {
    id: "timeline",
    question: "How soon do you need support?",
    options: ["As soon as possible", "This week", "This month", "I am just exploring options"],
  },
];

export const trustBadges = ["UK jobseeker support", "Tailored career guidance", "Remote help available"];

export const CheckIcon = CheckCircle2;
export const SparkIcon = Sparkles;